
import { v4 as uuidv4 } from 'uuid'
import { GeneralError } from '@/store/entities'
import { ValidationObserver } from 'vee-validate'
import { ImageUtils } from '@/commons/utils/image'
import { Component, Prop } from 'vue-property-decorator'
import { applicantStore, BiometryService } from '@/store'
import { ProviderInstance } from 'vee-validate/dist/types/types'
import { translateValidationError } from '@/commons/configuration/vee.validate'
import ModalBoxPhotoRequirements from '@/commons/components/modal-box/ModalBoxPhotoRequirements.vue'
import ModalBoxPhotoPreliminaryCheck from '@/commons/components/modal-box/ModalBoxPhotoPreliminaryCheck.vue'
import {
    ValidationError,
    ImageConstraints,
    BiometricImageComponent
} from '@/commons/types'
import { ApplicationType } from '@/commons/constants/application'
import $ from 'jquery'

@Component({
    components: {
        ModalBoxPhotoRequirements,
        ModalBoxPhotoPreliminaryCheck
    }
})
export default class BiometricImage extends ValidationObserver implements BiometricImageComponent {
    @Prop() ruleName!: string
    @Prop() serverSource!: string // person image from biometry buffer
    @Prop() defaultSource!: string // default image to display, when user has no uploaded image
    @Prop() imageSetter!: (value: unknown) => void
    @Prop() constraints!: ImageConstraints
    @Prop({ default: true }) isImageRequired!: boolean
    @Prop() validationProviderId!: string
    @Prop() userSelectedImage!: string | null
    @Prop({ default: false }) isPreliminaryCheckRequired!: boolean
    @Prop({ default: 'image/jpg,image/jpeg' }) allowedFileTypes!: string
    @Prop() applicationType!: ApplicationType
    @Prop({ default: '' }) alternateTextTranslationKey!: string

    private componentUUID = uuidv4()
    private preliminaryCheckErrors: Array<string> = []
    private defaultImage: string | null = null

    created (): void {
        this.defaultImage = require(`../../../../assets/biometry/${this.defaultSource}`)
    }

    get componentId (): string {
        return this.componentUUID
    }

    resetBiometry (): void {
        // Resetting component because of vee validate is not not handling input type file resetting at all
        this.componentUUID = uuidv4()
    }

    get isImage (): boolean {
        return !!(this.serverSource || this.userSelectedImage)
    }

    get hasMultipleImageSources (): boolean {
        return !!(this.serverSource && this.userSelectedImage && !this.isLoadingBiometry)
    }

    get isLoadingBiometry (): boolean {
        return applicantStore.biometry === null ||
            (this.isPreliminaryCheckRequired && applicantStore.isPhotoPreliminaryCheckRunning)
    }

    get imageRules (): Record<string, string | boolean | Array<string> | Record<string, number | string>> {
        return {
            required: !this.isImage && this.isImageRequired,
            mimes: this.constraints.allowedFileTypes.split(','),
            imageSize: {
                minBinary: this.constraints.minSizeBinary,
                maxBinary: this.constraints.maxSizeBinary,
                minText: this.constraints.minSizeText,
                maxText: this.constraints.maxSizeText
            },
            minDimensions: {
                width: this.constraints.minWidth,
                height: this.constraints.minHeight
            }
        }
    }

    async runPreliminaryCheck (photo: string): Promise<void> {
        if (!this.isPreliminaryCheckRequired) {
            return
        }
        const image = ImageUtils.stripJpegDataPrefix(photo)
        applicantStore.isPhotoPreliminaryCheckRunning = true
        this.preliminaryCheckErrors = await BiometryService.validatePhoto(applicantStore.personCode, image, this.applicationType)
        applicantStore.isPhotoPreliminaryCheckRunning = false
        if (this.preliminaryCheckErrors && this.preliminaryCheckErrors.length > 0) {
            $('#preliminaryCheckModal').modal('show')
        }
    }

    doOnUploadClicked (): void {
        if (this.isPreliminaryCheckRequired) {
            this.openPhotoRequirements()
        } else {
            this.openFileInput()
        }
    }

    openPhotoRequirements (): void {
        $('#photoRequirementsModal').modal('show')
    }

    openFileInput (): void {
        const fileInput = this.$refs.fileInput as HTMLInputElement
        fileInput.click()
    }

    resetImage (): void {
        this.imageSetter(null)
    }

    get imageUrl (): string | null {
        return ImageUtils.getBiometryImageFromUploadedOrExternalSource(
            this.userSelectedImage, this.serverSource
        ) || this.defaultImage
    }

    async handleFileInputChange (event: Event) {
        const files = (event.target as HTMLInputElement).files
        if (!files || files.length < 1) {
            return
        }
        const file = files[0] as File
        try {
            const provider = this.$refs.imageUpload as ProviderInstance
            const validationResult = await provider.validate(file)
            if (!validationResult.valid) {
                return
            }

            const base64String = await ImageUtils.fileToBase64(file)
            if (typeof base64String === 'string') {
                this.imageSetter(base64String)
                await this.runPreliminaryCheck(base64String)
            }
        } catch (e) {
            throw new GeneralError(['errors.system.image.invalid'])
        }
    }

    translateError (validationError: ValidationError): string {
        return translateValidationError(validationError)
    }
}
